/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

if (!customElements.get('shipping-price-calculator')) {
  class ShippingPriceCalculator extends HTMLElement {
    constructor() {
      super();
      this.shippingElement = document.querySelector('[data-merge="cart-shipping"] .theme-money');
      this.totalElement = document.querySelector('[data-merge="cart-total"] .theme-money');
      this.subtotalElement = document.querySelector('[data-merge="cart-subtotal"] .theme-money');
      
      this.calculateShipping();
      
      document.addEventListener('on:cart:after-merge', this.calculateShipping.bind(this));
      document.addEventListener('on:cart:add', this.calculateShipping.bind(this));
    }

    calculateShipping() {
      if (!this.subtotalElement || !this.shippingElement) return;
      
      const subtotalText = this.subtotalElement.textContent.trim();
      const subtotal = this.extractPrice(subtotalText);
      
      const freeShippingThreshold = 50000;
      
      const baseShippingCost = 2990;
      
      if (subtotal < freeShippingThreshold) {
        this.shippingElement.textContent = this.formatPrice(baseShippingCost);
        this.shippingElement.classList.remove('!hp-text-green-600');
      } else {
        this.shippingElement.textContent = 'Ücretsiz Kargo';
        this.shippingElement.classList.add('!hp-text-green-600');
      }

      const total = subtotal + (subtotal < freeShippingThreshold ? baseShippingCost : 0);
      if (this.totalElement) {
        this.totalElement.textContent = this.formatPrice(total);
      }
    }

    extractPrice(priceString) {
      return parseInt(priceString.replace(/[^0-9,]/g, '').replace(',', ''), 10);
    }

    formatPrice(amount) {
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount / 100);
    }
  }

  customElements.define('shipping-price-calculator', ShippingPriceCalculator);
}

/**
 * Handles the product button click based on the current stock status
 * @param {HTMLElement} button - The button element that was clicked
 */
function handleProductButton(button) {
  const stockStatus = button.getAttribute('data-stock-status');
  
  if (stockStatus === 'unavailable') {
    // Product is out of stock, open notification modal
    openHollyNotificationStockOutModal(button);
    return false; // Prevent form submission
  }
  
  // Product is in stock, let the form submit normally
  return true;
}

/**
 * Initialize the product variant change listener to update button state
 */
document.addEventListener('DOMContentLoaded', function() {
  const variantPicker = document.querySelector('variant-picker');
  
  if (variantPicker) {
    // Listen for variant change events
    variantPicker.addEventListener('on:variant:change', function(event) {
      const warningDiv = document.getElementById('variant-selection-warning');
      if (warningDiv) warningDiv.style.display = 'none';
      
      // Get the current variant
      const variant = event.detail.variant;
      
      const selectedOptions = event.detail.selectedOptions;
      
      const hasSizeOption = checkIfSizeOptionSelected();
      
      // Update all Add to Cart buttons
      const addToCartButtons = document.querySelectorAll('button[name="add"]');
      
      addToCartButtons.forEach(function(addToCartBtn) {
        if (addToCartBtn) {
          // Update the stock status attribute based on variant availability
          if (hasSizeOption && variant) {
            const isAvailable = variant.available;
            
            // Handle different naming conventions in different templates
            const currentStockStatus = addToCartBtn.getAttribute('data-stock-status');
            const usesInStockNaming = currentStockStatus === 'inStock' || currentStockStatus === 'noStock';
            
            // Set the appropriate status value based on the template's naming convention
            addToCartBtn.setAttribute(
              'data-stock-status', 
              isAvailable 
                ? (usesInStockNaming ? 'inStock' : 'available') 
                : (usesInStockNaming ? 'noStock' : 'unavailable')
            );
            
            // Update the variant ID to the current variant
            if (variant.id) {
              addToCartBtn.setAttribute('data-variant_id', variant.id);
            }
            
            // Update onClick handler based on variant availability
            if (isAvailable) {
              addToCartBtn.setAttribute('onClick', 'return checkVariantAvailability(this)');
            } else {
              addToCartBtn.setAttribute('onClick', 'openHollyNotificationStockOutModal(this); return false;');
            }
            
            // Explicitly set button type based on availability to prevent form submission
            addToCartBtn.setAttribute('type', isAvailable ? 'submit' : 'button');
            
            // Update button text
            if (isAvailable) {
              addToCartBtn.textContent = addToCartBtn.getAttribute('data-add-to-cart-text');
            } else {
              // This should match the translation used in the template
              addToCartBtn.textContent = theme.strings.noStock || 'Gelince Haber Ver';
            }
          } else {
            addToCartBtn.textContent = addToCartBtn.getAttribute('data-add-to-cart-text');
            addToCartBtn.setAttribute('onClick', 'return checkVariantAvailability(this)');
            addToCartBtn.setAttribute('type', 'submit');
          }
        }
      });
    });
  }
  
  function checkIfSizeOptionSelected() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return false;
    
    const sizeSelectors = Array.from(variantPicker.querySelectorAll('.option-selector')).filter(
      selector => selector.dataset.option && (selector.dataset.option.includes('Beden') || selector.dataset.option.includes('Size'))
    );
    
    if (sizeSelectors.length === 0) return true; // No size option to select
    
    if (sizeSelectors[0].dataset.selectorType === 'listed') {
      return !!sizeSelectors[0].querySelector('input:checked');
    } 
    else if (sizeSelectors[0].dataset.selectorType === 'dropdown') {
      const customSelect = sizeSelectors[0].querySelector('custom-select');
      const selectedOption = customSelect && customSelect.querySelector('.custom-select__option[aria-selected="true"]');
      return !!(selectedOption && selectedOption.dataset.value);
    }
    
    return false;
  }
});

/**
 * Checks if the current variant is available and handles the button action accordingly
 * @param {HTMLElement} button - The button element that was clicked
 * @returns {boolean} - Whether to allow form submission or not
 */
window.checkVariantAvailability = function(button) {
  const stockStatus = button.getAttribute('data-stock-status');
  
  // If the variant is unavailable, call the notification modal function
  if (stockStatus === 'unavailable' || stockStatus === 'noStock') {
    // Make sure the button type is explicitly set to "button" to prevent form submission
    button.setAttribute('type', 'button');
    
    // // Ensure all required data attributes are present for the original function
    // if (!button.hasAttribute('data-button_loader_size')) {
    //   button.setAttribute('data-button_loader_size', '20px');
    // }
    // if (!button.hasAttribute('data-button_loader_color')) {
    //   button.setAttribute('data-button_loader_color', '#fff');
    // }
    // if (!button.hasAttribute('data-button_loader_width')) {
    //   button.setAttribute('data-button_loader_width', '2px');
    // }
    // if (!button.hasAttribute('data-button_type')) {
    //   button.setAttribute('data-button_type', 'InStock');
    // }
    
    // Call the original notification modal function (defined in holly-notifications-common-file.js)
    if (typeof window.openHollyNotificationStockOutModal === 'function') {
      window.openHollyNotificationStockOutModal(button);
    }
    
    // Always prevent form submission for unavailable variants
    return false;
  }
  
  // If the variant is available, allow form submission
  return true;
}
